<script setup lang="ts">
/**
 * /how-to-write-a-resume — the guide for the biggest informational cluster in
 * this niche: what a resume is, how to make one, what to put on it, what it
 * looks like finished.
 *
 * Split of responsibility with the neighbouring pages, so none of them competes
 * with another for the same query:
 *   /templates              which layout, and the format decision
 *   /how-to-write-a-resume  what to write inside it   ← this page
 *   /ai-cover-letter        the letter that goes with it
 *
 * Carries both FAQPage and HowTo in the graph. The HowTo is legitimate here:
 * the page really is one procedure, start to finish, in a fixed order.
 */
import { RESUME_STEP_IDS } from '~/constants/guidePages'

const { t } = useI18n()
const { faqPageNode, howToNode } = useJsonLd()

definePageMeta({
  layout: false,
  auth: false,
})

// Its URL in Czech, German and Polish (constants/localizedRoutes.ts).
false && /*#__PURE__*/ defineI18nRoute({ paths: { cs: '/jak-napsat-zivotopis', de: '/lebenslauf-schreiben', pl: '/jak-napisac-cv' } })

/** Answered in prose on the page, and mirrored into FAQPage structured data. */
const FAQ_IDS = ['pages', 'font', 'references', 'photo', 'tense', 'gpa', 'standout', 'time'] as const

const faqItems = computed(() =>
  FAQ_IDS.map(id => ({
    id,
    question: t(`resumeGuide.faq.${id}.q`),
    answer: t(`resumeGuide.faq.${id}.a`),
  })),
)

const extraNodes = computed(() => [
  faqPageNode('/how-to-write-a-resume', faqItems.value),
  howToNode('/how-to-write-a-resume', {
    name: t('resumeGuide.heading'),
    description: t('resumeGuide.short.text'),
    // The page's own estimate for a first version, not a guess.
    totalTime: 'PT1H30M',
    steps: RESUME_STEP_IDS.map(id => ({
      name: t(`resumeGuide.steps.items.${id}.title`),
      text: t(`resumeGuide.steps.items.${id}.text`),
    })),
  }),
])
</script>

<template>
  <MarketingFeaturePage
    seo-key="howToWriteResume"
    path="/how-to-write-a-resume"
    :breadcrumb-title="t('resumeGuide.heading')"
    :extra-nodes="extraNodes"
  >
    <MarketingResumeGuide />

    <MarketingFaqSection
      :title="t('resumeGuide.faq.title')"
      :items="faqItems"
      name="resume-guide-faq"
    />
  </MarketingFeaturePage>
</template>
